/**
 * A device for the titles that have no coat of arms, and never did.
 *
 * Three hundred titles need something inside their crest frame. Twenty-two of
 * them bore real arms and get the real thing. The rest used to get a painted
 * shield invented on the spot: a field, a charge, a bordure to difference it
 * from its crown. It looked like heraldry and was not, which is exactly the
 * complaint - a fabricated coat of arms is a lie told confidently, and it
 * reads as one.
 *
 * What sits there now is a single device on the bare opening, with no shield
 * behind it. That is an honest object. It says "this is a title, here is a
 * mark for it" without claiming any lord ever bore it, and the crest frame
 * around it does the work the shield outline was doing.
 *
 * The devices themselves are drawn from the real vocabulary of the period and
 * chosen by region, so a German duchy gets an eagle or a cross and a Norse one
 * gets an axe. Nothing is attributed to anybody; the point is that the visual
 * language belongs to the right century and the right corner of the map.
 */
import type { ChargeKind, Tincture } from './blazon';

/**
 * Regional device pools.
 *
 * Deliberately narrow. Heraldry in 1212 is a small vocabulary used with great
 * repetition, and a wide pool of exotic charges would look more invented, not
 * less.
 */
const POOLS: Record<string, ChargeKind[]> = {
  /** France, England, Iberia, Italy: lions, eagles, crosses, lilies. */
  latin: ['lionRampant', 'lionPassant', 'cross', 'crossPatty', 'fleur', 'escutcheon'],
  /** The Empire and its marches, where the eagle dominates. */
  imperial: ['eagle', 'lionRampant', 'crossPatty', 'mullet'],
  /** The north: the axe of Olav, and the cross that came with the church. */
  norse: ['axe', 'cross', 'lionRampant', 'crown'],
  /** Rus and the Baltic, where the cross and the eagle travelled east. */
  slavic: ['cross', 'eagle', 'lionRampant', 'mullet'],
  /** Byzantium and the Aegean, where the cross is the constant. */
  greek: ['cross', 'crossPotent', 'eagle'],
  /**
   * The Islamic realms.
   *
   * These had no heraldry, so there is nothing to imitate and nothing to get
   * right. What they had were banners: Abbasid black, Almohad white, Ayyubid
   * yellow, and devices on coin and standard that were geometric rather than
   * figurative. A crescent or a star is the closest honest gesture; a lion
   * rampant on a shield would be fancy dress.
   */
  islamic: ['crescent', 'mullet', 'roundel'],
};

/** Which pool a crown belongs to. Anything unlisted reads as Latin. */
const REGION: Record<string, keyof typeof POOLS> = {
  k_germany: 'imperial',
  k_bohemia: 'imperial',
  k_burgundy: 'imperial',
  k_italy: 'imperial',
  k_norway: 'norse',
  k_sweden: 'norse',
  k_denmark: 'norse',
  k_poland: 'slavic',
  k_hungary: 'slavic',
  k_livonia: 'slavic',
  k_novgorod: 'slavic',
  k_vladimir: 'slavic',
  k_kyiv: 'slavic',
  k_galicia: 'slavic',
  k_bulgaria: 'slavic',
  k_serbia: 'slavic',
  k_thrace: 'greek',
  k_thessalonica: 'greek',
  k_achaea: 'greek',
  k_epirus: 'greek',
  k_nicaea: 'greek',
  k_trebizond: 'greek',
  k_georgia: 'greek',
  k_morocco: 'islamic',
  k_tlemcen: 'islamic',
  k_ifriqiya: 'islamic',
  k_andalusia: 'islamic',
  k_valencia: 'islamic',
  k_rum: 'islamic',
  k_egypt: 'islamic',
  k_baghdad: 'islamic',
  k_mosul: 'islamic',
  k_mardin: 'islamic',
  k_azerbaijan: 'islamic',
};

/** Metals only. A device with no shield behind it has to carry its own weight. */
const METALS: Tincture[] = ['or', 'argent'];

function hash(text: string): number {
  let h = 2_166_136_261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16_777_619);
  }
  return ((h >>> 8) % 100_000) / 100_000;
}

export interface Symbol {
  kind: ChargeKind;
  tincture: Tincture;
}

/**
 * The device for a title, stable for its name.
 *
 * Deterministic, so a duchy wears the same mark every time anybody looks at
 * it, on the map and in the panels alike.
 */
export function symbolFor(name: string, kingdomId: string | null): Symbol {
  const pool = POOLS[(kingdomId && REGION[kingdomId]) || 'latin']!;
  return {
    kind: pool[Math.floor(hash(name) * pool.length)]!,
    // Gold for most, silver for a minority, so a screen of them is not one flat
    // colour but is still obviously one family of things.
    tincture: METALS[hash(`${name}:metal`) < 0.72 ? 0 : 1]!,
  };
}
